import { useState } from 'react';
import { Button, Card, Code, Group, Text, Title } from '@mantine/core';

/**
 * A value that exists on this screen and nowhere else, ever again.
 *
 * An API key is stored as a hash and a webhook secret is handed to the receiving end at the moment
 * it is made, so what is shown here is not a policy about what we display, it is the truth about
 * what still exists. A publisher who scrolls past this has lost the value and has to rotate.
 *
 * So it is loud, it sits at the top of the section rather than in a dialog that a stray click can
 * close, and putting it away takes a deliberate press with the consequence written on it. Copying
 * can be refused by a browser, so the value is always visible to select by hand as well.
 */
export function SecretOnce({
  title,
  value,
  instruction,
  onDismiss,
}: {
  title: string;
  value: string;
  /** What the person is meant to do with it, in their own terms. */
  instruction: string;
  onDismiss: () => void;
}) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  }

  return (
    <Card
      withBorder
      radius="lg"
      p="lg"
      my="md"
      component="section"
      role="alert"
      style={{ borderColor: 'var(--mantine-primary-color-filled)', borderWidth: 2 }}
    >
      <Title order={3} size="h4">
        {title}
      </Title>
      <Text size="sm" mt={4} fw={500}>
        Copy it now. This is the only time it is shown, and we cannot show it again.
      </Text>
      <Text size="sm" c="dimmed" mt={4}>
        {instruction}
      </Text>

      <Code block mt="md" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
        {value}
      </Code>

      <Group mt="md" gap="md">
        <Button variant="default" onClick={() => void copy()}>
          Copy
        </Button>
        <Button variant="subtle" color="gray" onClick={onDismiss}>
          I have saved it, hide it
        </Button>
        {copyState === 'copied' && (
          <Text size="sm" c="violet" role="status">
            Copied to your clipboard.
          </Text>
        )}
        {copyState === 'failed' && (
          <Text size="sm" c="dimmed" role="status">
            Your browser would not let us copy. Select the value above and copy it yourself.
          </Text>
        )}
      </Group>
    </Card>
  );
}
